import React, { useContext, useState } from 'react';
import Text from '../../../Text';
import Toggle from '../../../Transition/Toggle';
import AccordionItemCaret from '../AccordionItemCaret';
import { timingMap } from '../../../Transition/constants';
import { BUTTON_TYPES, DATA_HOOKS } from '../../constants';
import { WixDesignSystemContext } from '../../../WixDesignSystemProvider/context';

import { classes } from './AccordionItem.classes';
import {
  AccordionItemCaretProps,
  AccordionItemProps,
} from '../../Accordion.types';

type IconsContainerProps = {
  position: 'start' | 'end';
  toggleIconPlacement: AccordionItemProps['toggleIconPlacement'];
  icon: React.ReactNode;
  iconDataHook?: string;
  caretProps: AccordionItemCaretProps;
};

const IconsContainer = ({
  position,
  toggleIconPlacement,
  icon,
  iconDataHook,
  caretProps,
}: IconsContainerProps) => {
  const shouldShowToggleIcon = toggleIconPlacement === position;

  return shouldShowToggleIcon || icon ? (
    <div className={classes.iconsContainer()}>
      {shouldShowToggleIcon && position === 'start' && (
        <AccordionItemCaret {...caretProps} />
      )}
      {icon && (
        <div className={classes.affix()} data-hook={iconDataHook}>
          {icon}
        </div>
      )}
      {shouldShowToggleIcon && position === 'end' && (
        <AccordionItemCaret {...caretProps} />
      )}
    </div>
  ) : null;
};

function AccordionItem({
  icon,
  prefix,
  suffix,
  title,
  subtitle,
  open,
  children,
  onToggle,
  onMouseLeave,
  onMouseEnter,
  disabled,
  skin,
  hideShadow,
  className,
  size,
  horizontalPadding,
  contentPadding,
  last,
  transitionSpeed = 'medium',
  onAnimationEnter,
  onAnimationExit,
  buttonType = 'textButton',
  expandLabel,
  collapseLabel,
  toggleIconPlacement = 'end',
  showLabel,
  titleSize,
}: AccordionItemProps) {
  // needed for toggle button label visibility
  const [hover, setHover] = useState(false);

  const { mobile } = useContext(WixDesignSystemContext);

  const isLabelShown = !!(showLabel === 'always' || hover || mobile);

  const caretProps = {
    hover,
    buttonType,
    expandLabel,
    collapseLabel,
    showLabel,
    isLabelShown,
    open,
    disabled,
    skin,
  };

  const caretWithLabel =
    buttonType === BUTTON_TYPES.textButton &&
    isLabelShown &&
    !!(open ? collapseLabel : expandLabel);

  const _onMouseLeave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setHover(false);
    !disabled && onMouseLeave && onMouseLeave(e);
  };

  const _onMouseEnter = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setHover(true);
    !disabled && onMouseEnter && onMouseEnter(e);
  };

  return (
    <div
      data-hook={DATA_HOOKS.item}
      className={classes.root(
        {
          disabled,
          open,
          skin,
          hideShadow,
          size,
          horizontalPadding,
          contentPadding,
          last,
          titleSize,
          transitionSpeed,
          caretWithLabel,
        },
        className,
      )}
    >
      <div
        onClick={!disabled ? onToggle : () => {}}
        className={classes.header()}
        data-hook={DATA_HOOKS.header}
        onMouseEnter={_onMouseEnter}
        onMouseLeave={_onMouseLeave}
      >
        <IconsContainer
          position="start"
          toggleIconPlacement={toggleIconPlacement}
          icon={prefix || icon}
          caretProps={caretProps}
          iconDataHook={DATA_HOOKS.prefix}
        />
        <div className={classes.titleWrapper()}>
          {title && (
            <div data-hook="titleContainer">
              {typeof title === 'string' ? (
                <Text
                  dataHook="title"
                  ellipsis
                  weight="normal"
                  className={classes.title()}
                >
                  {title}
                </Text>
              ) : (
                title
              )}
            </div>
          )}
          {subtitle && (
            <div data-hook={DATA_HOOKS.subtitle} className={classes.subtitle()}>
              {typeof subtitle === 'string' ? (
                <Text size="small">{subtitle}</Text>
              ) : (
                subtitle
              )}
            </div>
          )}
        </div>
        <IconsContainer
          position="end"
          toggleIconPlacement={toggleIconPlacement}
          icon={suffix}
          caretProps={caretProps}
          iconDataHook={DATA_HOOKS.suffix}
        />
      </div>
      <Toggle
        show={open}
        timeout={timingMap[transitionSpeed]}
        onStart={onAnimationEnter}
        onEnd={onAnimationExit}
        className={classes.panel()}
        unmountOnExit
      >
        <div data-hook="children" className={classes.content()}>
          {children}
        </div>
      </Toggle>
    </div>
  );
}

export default AccordionItem;
